import * as Sentry from "@sentry/browser";
import type { ConsolaOptions, ConsolaReporter, LogObject } from "consola";
import { LogLevels } from "consola";

import { env } from "../config/env";
import logger from "./logger";

/** Keys whose values never leave the browser in a report: credentials and the date of birth. */
const SECRET_KEY = /^(password|rePassword|old_password|new_password|birthDate|birth_date)$/i;
const SECRET_IN_TEXT =
  /("(?:password|rePassword|old_password|new_password|birthDate|birth_date)"\s*:\s*)"(?:[^"\\]|\\.)*"/gi;
/** The same inside a JSON string that was serialised again (`\"password\":\"…\"`, e.g. an axios error's config.data). */
const SECRET_IN_ESCAPED =
  /(\\"(?:password|rePassword|old_password|new_password|birthDate|birth_date)\\"\s*:\s*)\\"(?:(?!\\").)*\\"/gi;
export const FILTERED = "[Filtered]";

/** Redacts `"password":"…"` / `"birthDate":"…"` inside a string (e.g. a serialised request body). */
export function scrubText(text: string): string {
  return text
    .replace(SECRET_IN_ESCAPED, `$1\\"${FILTERED}\\"`)
    .replace(SECRET_IN_TEXT, `$1"${FILTERED}"`);
}

/**
 * A copy of `value` with the secret keys' values replaced and every string scrubbed (request bodies travel as JSON
 * strings in axios errors and breadcrumbs). Errors and other class instances are kept as they are.
 */
export function scrubSecrets<T>(value: T, depth = 0): T {
  if (typeof value === "string") return scrubText(value) as T;
  if (value === null || typeof value !== "object" || depth > 8) return value;
  if (Array.isArray(value)) return value.map((v) => scrubSecrets(v, depth + 1)) as T;
  const proto = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) return value;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = SECRET_KEY.test(k) ? FILTERED : scrubSecrets(v, depth + 1);
  }
  return out as T;
}

export class SentryReporter implements ConsolaReporter {
  log(logObj: LogObject, _ctx: { options: ConsolaOptions }): void {
    const isStackHelper = (a: unknown): a is Error =>
      a instanceof Error && a.name === "" && a.message.startsWith("\n[stack]");
    const message = scrubText(
      logObj.args
        .filter((a) => !isStackHelper(a))
        .map((a) => (typeof a === "string" ? a : JSON.stringify(a)))
        .join(" "),
    );
    const err = logObj.args.find(
      (a) => a instanceof Error && !isStackHelper(a),
    ) as Error | undefined;
    const tag = message.match(/^\[([^\]]+)\]/)?.[1];

    if (logObj.level <= LogLevels.error) {
      if (err) {
        Sentry.captureException(err, {
          tags: tag ? { module: tag } : undefined,
          extra: { message },
        });
      } else {
        Sentry.captureMessage(message, {
          level: "error",
          tags: tag ? { module: tag } : undefined,
        });
      }
    } else if (logObj.level === LogLevels.warn) {
      Sentry.addBreadcrumb({ level: "warning", message, category: tag });
    } else {
      Sentry.addBreadcrumb({
        level: logObj.level <= LogLevels.info ? "info" : "debug",
        message,
        category: tag,
      });
    }
  }
}

let reporter: SentryReporter | null = null;

/** Starts Sentry once. Called only after this device ACCEPTED crash_reports (see lib/deviceConsent). */
export function initSentry(): void {
  if (reporter) return;
  const dsn = env.SENTRY_DSN;
  if (!dsn) {
    console.log("[Sentry] DSN not set — skipping init");
    return;
  }

  console.log("[Sentry] Initializing", {
    dsn,
    environment: env.SENTRY_ENVIRONMENT,
  });
  Sentry.init({
    dsn,
    environment: env.SENTRY_ENVIRONMENT,
    release: __APP_VERSION__,
    // Request bodies (sign-up / sign-in / date of birth) must never reach a report: scrub every breadcrumb and event.
    beforeBreadcrumb(breadcrumb) {
      return scrubSecrets(breadcrumb);
    },
    beforeSend(event, hint) {
      const err = hint.originalException;
      if (err instanceof Error && err.name === "ChunkLoadError") return null;
      if (
        err != null &&
        typeof err === "object" &&
        "response" in err &&
        (err as { response?: { status?: number } }).response?.status === 401
      )
        return null;
      console.log(
        "[Sentry] beforeSend — sending event",
        event.event_id,
        event.exception?.values?.[0]?.value,
      );
      return scrubSecrets(event);
    },
  });

  reporter = new SentryReporter();
  logger.addReporter(reporter);
}

/** Stops Sentry after crash_reports is DECLINED: nothing more is sent from this page. */
export function closeSentry(): void {
  if (!reporter) return;
  logger.removeReporter(reporter);
  reporter = null;
  void Sentry.close();
}

export function isSentryStarted(): boolean {
  return reporter !== null;
}

export function setSentryUser(uid: string | null): void {
  Sentry.setUser(uid ? { id: uid } : null);
}
